import { useState, useMemo, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import * as docx from 'docx-preview';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configuration du worker PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FileViewerModalProps {
  url: string;
  nom?: string;
  onClose: () => void;
}

export default function FileViewerModal({ url, nom, onClose }: FileViewerModalProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1.25);
  const [rotation, setRotation] = useState(0);
  const docxRef = useRef<HTMLDivElement>(null);

  const fileType = useMemo(() => {
    const ext = url.split('.').pop()?.split('?')[0].toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext || '')) return 'image';
    if (['docx', 'dotx'].includes(ext || '')) return 'docx';
    return 'other';
  }, [url]);

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.2, 3.0));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  const handleResetZoom = () => setScale(1.25);
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  // DOCX Rendering Logic
  useEffect(() => {
    if (fileType === 'docx' && docxRef.current) {
      const renderDocx = async () => {
        try {
          setLoading(true);
          const response = await fetch(url);
          const blob = await response.blob();
          
          if (docxRef.current) {
            docxRef.current.innerHTML = ''; // Clear previous
            await docx.renderAsync(blob, docxRef.current, undefined, {
              className: "docx-viewer",
              inWrapper: true,
              ignoreLastRenderedPageBreak: false,
            });
          }
          setLoading(false);
        } catch (err) {
          console.error("❌ Docx rendering error:", err);
          setError("Impossible de prévisualiser ce document Word. Il est peut-être protégé ou le format n'est pas supporté.");
          setLoading(false);
        }
      };
      renderDocx();
    }
  }, [url, fileType]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-xl sm:rounded-[32px] w-full max-w-5xl h-full max-h-[95vh] flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 border border-white/20">
        
        {/* Header Premium */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-white">
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="font-black text-[#1B3A2F] italic truncate flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm border border-gray-50 ${
                fileType === 'pdf' ? 'bg-rose-50 text-rose-500' : 
                fileType === 'image' ? 'bg-blue-50 text-blue-500' : 
                fileType === 'docx' ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-600'
              }`}>
                <i className={
                  fileType === 'pdf' ? 'ri-file-pdf-fill text-xl' : 
                  fileType === 'image' ? 'ri-image-fill text-xl' : 
                  fileType === 'docx' ? 'ri-file-word-2-fill text-xl' : 'ri-file-text-fill text-xl'
                }></i>
              </div>
              <span className="truncate">{nom || 'Aperçu du document'}</span>
            </h3>
            {fileType === 'pdf' && numPages && (
              <p className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mt-1 ml-13">
                {numPages} page(s) • DOCUMENT PDF
              </p>
            )}
            {fileType === 'image' && (
              <p className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mt-1 ml-13">
                IMAGE HAUTE RÉSOLUTION
              </p>
            )}
            {fileType === 'docx' && (
              <p className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mt-1 ml-13">
                MICROSOFT WORD (.DOCX)
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Zoom Controls (Images, PDF, DOCX) */}
            {(fileType === 'pdf' || fileType === 'image' || fileType === 'docx') && (
              <div className="hidden sm:flex items-center space-x-1 bg-gray-50 p-1.5 rounded-xl border border-gray-100 mr-2">
                <button 
                  type="button"
                  onClick={handleZoomOut} 
                  className="w-8 h-8 flex items-center justify-center hover:bg-white rounded-lg transition-all text-gray-600 shadow-sm hover:shadow-md cursor-pointer" 
                  title="Zoom arrière"
                >
                  <i className="ri-zoom-out-line"></i>
                </button>
                <span className="text-[10px] font-black w-12 text-center text-[#1B3A2F] uppercase tracking-tight">
                  {Math.round(scale * 100)}%
                </span>
                <button 
                  type="button"
                  onClick={handleZoomIn} 
                  className="w-8 h-8 flex items-center justify-center hover:bg-white rounded-lg transition-all text-gray-600 shadow-sm hover:shadow-md cursor-pointer" 
                  title="Zoom avant"
                >
                  <i className="ri-zoom-in-line"></i>
                </button>
                <button 
                  type="button"
                  onClick={handleRotate} 
                  className="w-8 h-8 flex items-center justify-center hover:bg-white rounded-lg transition-all text-gray-600 shadow-sm hover:shadow-md cursor-pointer ml-1" 
                  title="Faire pivoter"
                >
                  <i className="ri-clockwise-line text-xl"></i>
                </button>
              </div>
            )}

            <a
              href={url}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-50 text-green-600 hover:bg-green-100 transition-all cursor-pointer border border-green-100/50"
              title="Télécharger le fichier"
            >
              <i className="ri-download-2-line text-xl"></i>
            </a>
            
            <button 
              type="button"
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-rose-50 text-rose-500 hover:bg-rose-100 transition-all cursor-pointer border border-rose-100/50"
              title="Fermer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-[#F8FAFC] relative">
          <div className="flex flex-col items-center py-10 min-h-full">
            
            {loading && fileType !== 'other' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 z-10 backdrop-blur-[2px]">
                <div className="animate-spin w-14 h-14 border-4 border-[#1B3A2F]/10 border-t-[#FFD700] rounded-full mb-4 shadow-xl"></div>
                <p className="text-[11px] font-black text-[#1B3A2F] uppercase tracking-[0.2em] italic animate-pulse">
                  Chargement de l'aperçu...
                </p>
              </div>
            )}

            {error && (
              <div className="bg-rose-50 border border-rose-100 text-rose-700 px-8 py-6 rounded-[24px] flex flex-col items-center max-w-md mx-auto shadow-xl">
                <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mb-4 text-rose-600 text-2xl">
                  <i className="ri-error-warning-line"></i>
                </div>
                <h4 className="font-black italic text-lg mb-2">Erreur d'affichage</h4>
                <p className="text-sm text-center font-medium opacity-80 mb-6">{error}</p>
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="px-6 py-2 bg-rose-600 text-white rounded-xl font-bold text-[10px] uppercase tracking-widest shadow-lg shadow-rose-600/20"
                >
                  Ouvrir dans un nouvel onglet
                </a>
              </div>
            )}

            {/* PDF RENDERING */}
            {fileType === 'pdf' && !error && (
              <Document
                file={url}
                onLoadSuccess={({ numPages }) => {
                  setNumPages(numPages);
                  setLoading(false);
                  setError(null);
                }}
                onLoadError={(err) => {
                  console.error('❌ PDF load error:', err);
                  setError("Le document PDF n'a pas pu être chargé. Il se peut qu'il soit protégé ou corrompu.");
                  setLoading(false);
                }}
                loading={null}
                error={null}
              >
                {numPages && Array.from(new Array(numPages), (_, index) => (
                  <div key={`page_${index + 1}`} className="mb-10 last:mb-0 transform transition-all duration-300">
                    <div className="bg-white shadow-[0_20px_50px_rgba(0,0,0,0.1)] rounded-lg overflow-hidden transition-all mx-4 inline-block border border-gray-100">
                      <Page
                        pageNumber={index + 1}
                        scale={scale}
                        rotate={rotation}
                        width={Math.min(window.innerWidth * 0.9, 850)}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className="mx-auto"
                      />
                    </div>
                    <p className="text-center text-[10px] font-black text-gray-600 uppercase tracking-widest mt-4 italic">
                      Page {index + 1} sur {numPages}
                    </p>
                  </div>
                ))}
              </Document>
            )}

            {/* IMAGE RENDERING */}
            {fileType === 'image' && !error && (
              <div className="px-4 flex flex-col items-center">
                <div 
                  className="bg-white shadow-[0_30px_60px_rgba(0,0,0,0.15)] rounded-[24px] overflow-hidden border border-gray-100 transition-all duration-300"
                  style={{ transform: `scale(${scale}) rotate(${rotation}deg)`, transformOrigin: 'center center' }}
                >
                  <img 
                    src={url} 
                    alt={nom || 'Aperçu'} 
                    className="max-w-[90vw] sm:max-w-4xl h-auto"
                    onLoad={() => setLoading(false)}
                    onError={() => {
                      setError("L'image n'a pas pu être chargée.");
                      setLoading(false);
                    }}
                  />
                </div>
              </div>
            )}

            {/* DOCX RENDERING */}
            {fileType === 'docx' && !error && (
              <div className="px-4 w-full flex flex-col items-center">
                <div 
                  ref={docxRef}
                  className="bg-white shadow-[0_30px_60px_rgba(0,0,0,0.15)] rounded-[24px] p-8 sm:p-12 border border-gray-100 transition-all duration-300 w-full max-w-4xl"
                  style={{ transform: `scale(${scale}) rotate(${rotation}deg)`, transformOrigin: 'center top' }}
                >
                  {/* Word content will be rendered here by docx-preview */}
                </div>
              </div>
            )}

            {/* FALLBACK FOR OTHER TYPES */}
            {fileType === 'other' && (
              <div className="py-20 flex flex-col items-center justify-center px-6">
                <div className="w-24 h-24 rounded-[32px] bg-white shadow-xl flex items-center justify-center text-gray-500 text-5xl mb-8 border border-gray-50">
                  <i className="ri-file-unknow-line"></i>
                </div>
                <h4 className="text-xl font-black text-[#1B3A2F] italic mb-3 text-center">
                  Prévisualisation non disponible
                </h4>
                <p className="text-sm text-gray-500 font-medium text-center max-w-sm mb-10">
                  Ce type de fichier ({url.split('.').pop()?.toUpperCase()}) ne peut pas être visualisé directement dans le navigateur.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                  <a
                    href={url}
                    download
                    className="px-10 py-4 bg-[#1B3A2F] text-white rounded-[20px] font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-3 shadow-xl shadow-[#1B3A2F]/20 hover:scale-105 transition-all"
                  >
                    <i className="ri-download-2-line text-lg"></i>
                    Télécharger le document
                  </a>
                  <button
                    onClick={onClose}
                    className="px-10 py-4 bg-gray-100 text-gray-500 rounded-[20px] font-black text-[11px] uppercase tracking-widest hover:bg-gray-200 transition-all"
                  >
                    Fermer l'aperçu
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
